import * as React from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '@/components/ui/button'
import { BackButton } from '@/components/ui/BackButton'
import { useToast } from '@/components/ui/use-toast'
import { ErrorDisplay } from '@/components/ui/error-display'
import { Skeleton } from '@/components/ui/skeleton'
import { OfferDetails, useOffer, useUpdateOffer, type UpdateOfferInput } from '@/lib/api/offers'
import { parseTRPCError, type ParsedTRPCError } from '@/lib/utils/trpc-error-utils'
import { OfferForm } from '@/components/form/offer/offer-form'
import { offerFormSchema, type OfferFormData } from '@/components/form/offer/validation'
import { format } from 'date-fns'
import { useStoreNavigate } from '@/hooks/use-app-navigate'

/**
 * Parses offer details to form data format
 */
const parseOfferToFormData = (offer: OfferDetails): OfferFormData => {
    return {
        titleEn: offer.titleTrans?.en || '',
        type: offer.type as 'flash' | 'bundle' | 'club',
        expiryDate: offer.expiryDate ? format(new Date(offer.expiryDate), "yyyy-MM-dd'T'HH:mm") : undefined,
        lineItems: offer.offerLineItems.map((item) => ({
            productId: item.price?.variant?.product?.id || '',
            productName: item.price?.variant?.product?.name || '',
            variantId: item.variantId,
            variantName: item.price?.variant?.name || '',
            imageUrl: item.price?.variant?.imageUrl || null,
            priceId: item.priceId,
            quantity: item.quantity,
        })),
        promotionIds: offer.promotions?.map((p) => p.promotionId) || [],
    }
}

/**
 * Skeleton component for loading state
 */
const EditOfferPageSkeleton = () => {
    return (
        <div className="bg-background min-h-screen">
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-8 w-8" />
                        <div>
                            <Skeleton className="h-8 w-64" />
                            <Skeleton className="mt-1 h-4 w-96" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Skeleton className="h-10 w-24" />
                        <Skeleton className="h-10 w-32" />
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="rounded-lg border">
                        <div className="p-6 space-y-6">
                            <Skeleton className="h-6 w-32" />
                            <div className="space-y-4">
                                <Skeleton className="h-10 w-full" />
                                <Skeleton className="h-10 w-full" />
                                <Skeleton className="h-10 w-full" />
                            </div>
                        </div>
                    </div>
                    <div className="rounded-lg border">
                        <div className="p-6 space-y-6">
                            <Skeleton className="h-6 w-32" />
                            <div className="space-y-4">
                                <Skeleton className="h-24 w-full" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

/**
 * Container component that loads offer data
 */
const EditOfferPageContainerInner = () => {
    const { id: storeId, offerId } = useParams<{ id: string; offerId: string }>()
    const { t } = useTranslation(['stores', 'common'])

    if (!storeId || !offerId) {
        throw new Error('Store ID and Offer ID are required')
    }

    const { data, isLoading, error } = useOffer(offerId)

    if (isLoading) {
        return <EditOfferPageSkeleton />
    }

    if (error) {
        return <ErrorDisplay error={parseTRPCError(error)} />
    }

    if (!data?.offer) {
        return (
            <div className="container mx-auto py-8">
                <p className="text-red-500">{t('stores:offerNotFound')}</p>
            </div>
        )
    }

    return <EditOfferPage offer={data.offer} />
}

/**
 * Page for editing an offer
 */
export const EditOfferPage = ({ offer }: { offer: OfferDetails }) => {
    const { t } = useTranslation(['stores', 'common'])
    const { id: storeId, offerId } = useParams<{ id: string; offerId: string }>()

    if (!storeId || !offerId) {
        throw new Error('Store ID and Offer ID are required')
    }

    const { storeRoutes, navigate } = useStoreNavigate();
    const { toast } = useToast()

    const updateOfferMutation = useUpdateOffer()
    const [formError, setFormError] = React.useState<ParsedTRPCError | null>(null)

    const formData = parseOfferToFormData(offer)
    const methods = useForm<OfferFormData>({
        defaultValues: formData,
        resolver: zodResolver(offerFormSchema),
        mode: 'onBlur',
    })


    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods


    const handleUpdateOffer = async (data: OfferFormData) => {
        try {
            console.log(data)
            setFormError(null)
            const input: UpdateOfferInput = {
                type: data.type,
                offerId,
                storeId,
                titleTrans: {
                    en: data.titleEn,
                },
                expiryDate: data.expiryDate ? new Date(data.expiryDate) : undefined,
                lineItems: data.lineItems.map((item) => ({
                    priceId: item.priceId,
                    quantity: item.quantity,
                })),
                promotionIds: data.promotionIds,
            }

            await updateOfferMutation.mutateAsync(input)

            toast({
                title: t('common:success'),
                description: t('stores:offerUpdatedSuccess'),
            })
            navigate(storeRoutes.OFFERS())
        } catch (error) {
            const parsedError = parseTRPCError(error)
            setFormError(parsedError)

            if (!parsedError.isValidationError) {
                toast({
                    title: t('common:error'),
                    description: parsedError.message,
                    variant: 'destructive',
                })
            }
        }
    }

    return (
        <div className="bg-background overflow-hidden">
            <div className="flex flex-col space-y-4 sm:space-y-6">
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(handleUpdateOffer)}>
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <BackButton to={storeRoutes.OFFERS()} />
                                <div>
                                    <h1 className="text-2xl font-semibold">{t('stores:editOffer')}</h1>
                                    <p className="text-muted-foreground text-sm">{t('stores:editOfferDescription')}</p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => navigate(storeRoutes.OFFERS())}
                                    disabled={isSubmitting}
                                >
                                    {t('common:cancel')}
                                </Button>
                                <Button type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? t('stores:updating') : t('stores:updateOffer')}
                                </Button>
                            </div>
                        </div>

                        {formError && !formError.isValidationError && (
                            <div className="mb-6">
                                <ErrorDisplay error={formError} />
                            </div>
                        )}

                        <OfferForm />
                    </form>
                </FormProvider>
            </div>
        </div>
    )
}

export const EditOfferPageContainer = withRequiredPermission(EditOfferPageContainerInner, PERMISSIONS.STORES_MANAGE)
export default EditOfferPageContainer

